import { useState, useEffect } from 'react';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// components
import Breadcrumbs from '../../../components/breadcrumbs';
import TableInfo from '../../../components/table-info';
import Pagination from '../../../components/pagination';
import Dialog from '../../../components/dialog';
// @devextreme
import DateBox from 'devextreme-react/date-box';
import DateRangeBox from 'devextreme-react/date-range-box';
import { Template } from 'devextreme-react/core/template';
import DataGrid, { Column } from 'devextreme-react/data-grid';
// @moment
import moment from 'moment-timezone';
// utils
import { timezones } from '../../../utils/format';
// assets
import Calendar from '../../../assets/button/Calendar';
// styles
import { Container, SearchWrapper, Box, PrimaryButton } from '../../../styles';
// apis
import { getLoginLogs } from '../../../apis';

// ----------------------------------------------------------------------

const sizes = [10, 20, 30];

const startDate = timezones(moment().utc().subtract(1, 'months').format(), 'YYYY-MM-DDTHH:mm:ss');
const now = timezones(moment().utc().format(), 'YYYY-MM-DDTHH:mm:ss');

// ----------------------------------------------------------------------

export default function LoginLogsPage() {
  const mobile = useResponsive('down', 'md');
  
  const [ date, setDate ] = useState([new Date(startDate), new Date(now)]);
  
  const [ data, setData ] = useState({ content: [], totalElements: 0, totalPages: 1 });
  const { content: rows, totalElements: total, totalPages: pages } = data;

  const [ target, setTarget ] = useState('organization');
  const [ keyword, setKeyword ] = useState('');
  const [ page, setPage ] = useState(0);
  const [ size, setSize ] = useState(20);

  const [ message, setMessage ] = useState(null);
  const handleClose = () => setMessage(null);

  useEffect(() => {
    ( async () =>
      await getLoginLogs(setData, date[0], date[1], search(), page, size)
    )();
  }, []);

  const search = (reset?: boolean) => {
    if ( reset ) return { organization: '', identification: '' };
    return {
      organization: target === 'organization' ? keyword : '',
      identification: target === 'identification' ? keyword : ''
    }
  };

  const handlePage = (value: number) => {
    setPage(value-1);
    getLoginLogs(setData, date[0], date[1], search(), value-1, size);
  };

  const handleSize = (value: number) => {
    setPage(0);
    setSize(value);
    getLoginLogs(setData, date[0], date[1], search(), 0, value);
  };

  const handleSearch = () => {
    const difference = moment(date[1]).diff(moment(date[0]), 'months');
    if ( difference > 2 ) return setMessage('3개월 이내로 검색해 주세요.');

    if ( date[0] >  date[1] ) return setMessage('검색 시작 시간은 종료 시간보다 이전이어야 합니다.\n검색 기간을 다시 한번 확인해 주세요.');
    if ( moment(date[1]).isAfter(now, 'day') ) return;

    if ( keyword.length > 0 && keyword.replace(/ /g, '').length === 0 ) return;

    setPage(0);
    getLoginLogs(setData, date[0], date[1], search(), 0, size);
  };

  const cellRender = ({data}: any) => {
    return timezones(data.accessAt, 'YYYY-MM-DD HH:mm:ss');
  };

  return (
    <Container>
      <Breadcrumbs
        heading='로그인 조회'
        links={[
          {name: '계정 관리'},
          {name: '로그인 조회'}
        ]}
      />

      <SearchWrapper>
        <div>
          { !mobile ? (
            <DateRangeBox
              value={date}
              onValueChanged={(event: any) => setDate(event.value) }
              displayFormat='yyyy-MM-dd'
              openOnFieldClick={true}
              useMaskBehavior={true}
              startDateLabel=''
              endDateLabel=''
              max={new Date(now)}
              dropDownButtonTemplate='calendar'
            >
              <Template name='calendar' render={Calendar} />
            </DateRangeBox>
          ) : (
            <div className='rangeBox' style={{'width': '100%'}}>
              <DateBox
                type='date'
                value={date[0]}
                onValueChanged={(event) => setDate([event.value, date[1]])}
                displayFormat='yyyy-MM-dd'
                elementAttr={{class: 'calendarStart'}}
                openOnFieldClick={true}
                useMaskBehavior={true}
                pickerType='rollers'
                max={new Date(now)}
              />
              <p className='range'>~</p>
              <DateBox
                type='date'
                value={date[1]}
                onValueChanged={(event) => setDate([date[0], event.value])}
                displayFormat='yyyy-MM-dd'
                elementAttr={{class: 'calendarEnd'}}
                openOnFieldClick={true}
                useMaskBehavior={true}
                pickerType='rollers'
                max={new Date(now)}
                dropDownButtonTemplate='calendar'
              >
                <Template name='calendar' render={Calendar} />
              </DateBox>
            </div>
          )}

          <select value={target} onChange={(event) => setTarget(event.target.value)}>
            <option value='organization'>조직</option>
            <option value='identification'>아이디</option>
          </select>

          <input type='text' placeholder='검색어를 입력하세요.' onChange={(event) => setKeyword(event.target.value)} onKeyDown={(event) => { if(event.key === 'Enter') handleSearch() }} />
          <PrimaryButton type='button' onClick={() => handleSearch()}>검색</PrimaryButton>
        </div>

        <div />
      </SearchWrapper>
      
      <Box>
        <TableInfo total={total} pages={pages} page={page} sizes={sizes} size={size} handleSize={handleSize} />

        <DataGrid
          dataSource={rows}
          noDataText=''
          columnAutoWidth={true}
          paging={{enabled: false}}
        >
          <Column caption='일시' dataField='accessAt' alignment='center' cellRender={cellRender} />
          <Column caption='로그인 아이디' dataField='requestId' alignment='center' />
          <Column caption='조직' dataField='organization' alignment='center' />
          <Column caption='접속 유형' dataField='device' alignment='center' />
          <Column caption='운영 체제' dataField='os' alignment='center' />
          <Column caption='브라우저 / 앱' dataField='browser' alignment='center' />
          <Column caption='로그인 IP' dataField='ip' alignment='center' />
          <Column caption='상태' dataField='status' alignment='center' />
        </DataGrid>

        <Pagination page={page} pages={pages} size={size} handlePage={handlePage} />
      </Box>

      { message &&
        <Dialog
          handleClose={handleClose}
        >
          <p>{ message }</p>
        </Dialog>
      }
    </Container>
  );
}